import type { LicenceStandingKey } from '@fieldops/shared'
import type { Tone } from '../components/ui.js'

/**
 * How a ticket's standing reads, wherever it is drawn.
 *
 * One definition, because the same five words appear on the job card's service
 * brief, on a row of the people list and twice on the certifications screens. A
 * second copy would drift, and the version of that bug that matters is the one
 * where a licence reads "Current" on one screen and "Expired" on another.
 *
 * The standing itself is computed by `licenceStanding` in `@fieldops/shared` and
 * never stored — see `_agent/sa-pest-licensing.md`.
 */

export function standingBadge(standing: string): { label: string; tone: Tone } {
  switch (standing) {
    case 'held':
      return { label: 'Current', tone: 'green' }
    case 'due':
      return { label: 'Due', tone: 'amber' }
    case 'expired':
      return { label: 'Expired', tone: 'red' }
    case 'undated':
      return { label: 'No date', tone: 'amber' }
    default:
      return { label: 'Not held', tone: 'red' }
  }
}

/**
 * Worst first.
 *
 * A person is described by their worst ticket, because that is the one somebody
 * has to do something about. Shared with the API's `standingByUser`, which sorts
 * the same way for the same reason.
 */
export const STANDING_ORDER: LicenceStandingKey[] = [
  'expired',
  'missing',
  'undated',
  'due',
  'held',
]

/** The chips above the certifications table, in the order they are read. */
export const STANDING_FILTERS: ReadonlyArray<{ key: LicenceStandingKey; label: string }> = [
  { key: 'expired', label: 'Expired' },
  { key: 'due', label: 'Due soon' },
  { key: 'missing', label: 'Missing' },
  { key: 'undated', label: 'No date' },
  { key: 'held', label: 'Current' },
]

/**
 * Whether a ticket is somebody's job to fix.
 *
 * Expired and undated always are. Not holding one is only when it is expected
 * of field staff and this person goes out, which is what `isGap` already says
 * on the server — the office manager without a termite endorsement is not a
 * finding.
 */
export function needsAttention(standing: string, isGap: boolean): boolean {
  return standing === 'expired' || standing === 'undated' || isGap
}

export function attentionLabel(count: number): string {
  return count === 0 ? 'All in order' : `${count} ${count === 1 ? 'needs' : 'need'} attention`
}

/**
 * The seeded catalogue's column headings.
 *
 * The full names are sentences ("FPMT — fumigation (soil, stack, silo and
 * ships)") and the grid sets its headings on end in 112px. The catalogue is
 * `LICENCE_TYPES` in the seed, keyed by these stable ids, and has no editor;
 * anything else falls back to its name without the brackets.
 */
const SHORT_NAMES: Record<string, string> = {
  lictype_fpmt_domestic_commercial: 'FPMT excl. timber',
  lictype_fpmt_timber_pest: 'FPMT incl. timber',
  lictype_fpmt_fumigation: 'Fumigation',
  lictype_fpmt_vertebrate: 'Vertebrate',
  lictype_fpmt_weeds: 'Weeds',
  lictype_lpmt: 'LPMT',
  lictype_confined_space: 'Confined space',
  lictype_working_at_height: 'Working at height',
  lictype_first_aid: 'First aid',
  lictype_swms_subfloor: 'SWMS subfloor',
  lictype_swms_residential: 'SWMS residential',
  lictype_swms_height: 'SWMS roof',
}

export function licenceShortName(type: { id: string; name: string }): string {
  const known = SHORT_NAMES[type.id]
  if (known) return known

  const plain = type.name.replace(/\s*\(.*?\)\s*/g, ' ').replace(/\s+—\s+/g, ' ').trim()
  return plain.length > 20 ? `${plain.slice(0, 19).trimEnd()}…` : plain
}

/** A `Date` as the `yyyy-mm-dd` an `<input type="date">` wants, in local parts. */
export function dateInputValue(value: Date | string | null | undefined): string {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`
}

/** "in 34 days", "12 days ago", or a dash where there is no date to count to. */
export function daysLeftLabel(daysLeft: number | null): string {
  if (daysLeft === null) return '—'
  if (daysLeft === 0) return 'Today'
  if (daysLeft === 1) return 'Tomorrow'
  if (daysLeft < 0) return `${Math.abs(daysLeft)} days ago`
  return `In ${daysLeft} days`
}

/** The sentence under a ticket, in the words the job card already uses. */
export function standingDetail(entry: {
  standing: string
  daysLeft: number | null
  licenceType: { authority: string | null; expires: boolean }
}): string {
  const authority = entry.licenceType.authority

  switch (entry.standing) {
    case 'held':
      return entry.daysLeft === null
        ? [authority, 'Signed off, no expiry'].filter(Boolean).join(' · ')
        : `Current for another ${entry.daysLeft} days`
    case 'due':
      if (entry.daysLeft === 0) return 'Expires today.'
      if (entry.daysLeft === 1) return 'Expires tomorrow.'
      return `Expires in ${entry.daysLeft} days. Renewals take weeks, so start now.`
    case 'expired':
      return 'Lapsed. This person cannot do work that needs it until it is renewed.'
    case 'undated':
      return 'Recorded, but with no expiry date. Find the date and put it in.'
    default:
      return 'Nothing recorded.'
  }
}
